import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import { RiFileExcel2Line } from "react-icons/ri";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import { FaDatabase, FaChartPie } from "react-icons/fa";
import Snowflake from "../../assets/Snowflake.png";
import Thoughtspot from "../../assets/thoughtspot-logos.png";
import KnowledgeGraphVizContainer from "../../containers/configuration/mkg/KnowledgeGraphVizContainer";
import MetricListComponent from "../../components/configuration/MetricListComponent";
import BenchmarkListComponent from "../../components/configuration/BenchmarkListComponent";
import SyncTSworksheet from "../../containers/configuration/SyncTSworksheet";
import CreateDataModelContainer from "../../containers/configuration/snowflake/CreateDataModelContainer";
import AddDescriptionContainer from "../../containers/configuration/snowflake/AddDescriptionContainer";
import ViewMetricByModelContainer from "../../containers/configuration/ViewMetricByModelContainer";
import { Header, ModelInfo } from "build-ts/metadata";
import KGColumnListComponent from "../../components/configuration/KGColumnListComponent";
import { ColumnType } from "build-ts/enums";
import { ConfigurationContextProvider } from "context/ConfigurationContext";
import IntegrationPage from "pages/integration/IntegrationPage";
import { getAllKGColumns } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import { IoGitCompareOutline } from "react-icons/io5";
import { MdBarChart, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { IoIosReturnRight } from "react-icons/io";
import ThoughtSpotEmbed from "../../components/configuration/ThoughtSpotEmbed";
import { useUser } from "../../context/UserContext";

const ConfigurationPage: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: isKGOpen } = useDisclosure({ defaultIsOpen: true });
  const { userId } = useUser();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [activeButton, setActiveButton] = useState<string>("ViewMKGContainer");
  const [selectedLink, setSelectedLink] = useState<string>("ViewMKGContainer");
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null);
  const [showMetricsInSidebar, setShowMetricsInSidebar] = useState<boolean>(false);
  const [showBenchmarksInSidebar, setShowBenchmarksInSidebar] = useState<boolean>(false);
  const [showKGColumnsInSidebar, setShowKGColumnsInSidebar] = useState<boolean>(false);
  const [showAttributesInSidebar, setShowAttributesInSidebar] = useState<boolean>(false);
  const [showMeasuresInSidebar, setShowMeasuresInSidebar] = useState<boolean>(false);
  const [showTemporalInSidebar, setShowTemporalInSidebar] = useState<boolean>(false);
  const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
  const [attributeList, setAttributeList] = useState<Header[]>([]);
  const [measureList, setMeasureList] = useState<Header[]>([]);
  const [temporalList, setTemporalList] = useState<Header[]>([]);
  const [showMKGNetwork, setShowMKGNetwork] = useState<boolean>(true); // Default to true to show on page load
  const [shouldReloadKG, setShouldReloadKG] = useState<boolean>(false); // Boolean to trigger reloads

  const fetchKgColumns = async () => {
    try {
      const kgColumns = await getAllKGColumns();
      if (kgColumns) {
        setAttributeList(kgColumns.filter((column: Header) => column.column_type === ColumnType.ATTRIBUTE));
        setMeasureList(kgColumns.filter((column: Header) => column.column_type === ColumnType.MEASURE));
        setTemporalList(kgColumns.filter((column: Header) => column.column_type === ColumnType.TEMPORAL));
      }
    } catch (error) {
      showToast("Error fetching KG columns", "", "error");
    }
  };
  const handleSnowflake = async (data: ModelInfo) => {
    setModelInfo(data);
    setActiveButton("snowflake");
    setSelectedLink("createModel");
  };

  const handleThoughtSpot = async (data: ModelInfo) => {
    setModelInfo(data);
    setActiveButton("thoughtspot");
    setSelectedLink("createModel");
  };

  const handleCreateDataModel = async () => {
    setShowMKGNetwork(false);
    setActiveButton("createModel");
    setSelectedLink("createModel");
  };

  // Function to navigate to the Graph from the Add Description Container (Data Model Creation or Updation Page)
  const handleNavigateToGraph = () => {
    setShowMKGNetwork(true);
    setActiveButton("ViewMKGContainer");
    setSelectedLink("ViewMKGContainer");
    setModelInfo(null);
  };

  // Function to reload the Knowledge Graph Viz Container
  const handleReloadKnowledgeGraph = () => {
    // Set to true to trigger reload, then immediately back to false
    setShouldReloadKG(true);
    setTimeout(() => setShouldReloadKG(false), 100);
  };

  const handleBackToSidebar = () => {
    setShowMetricsInSidebar(false);
    setShowBenchmarksInSidebar(false);
    setShowAttributesInSidebar(false);
    setShowMeasuresInSidebar(false);
    setShowTemporalInSidebar(false);
  };

  const renderComponent = () => {
    // If showMKGNetwork is true, always show ViewMKGContainer
    if (showMKGNetwork) {
      return <KnowledgeGraphVizContainer shouldReload={shouldReloadKG} />;
    }

    // Otherwise, show the selected component
    const componentMapping: Record<string, JSX.Element | null> = {
      thoughtspot: <SyncTSworksheet handleThoughtSpot={handleThoughtSpot} />,
      snowflake: <CreateDataModelContainer modelInfo={handleSnowflake} />,
      createModel: (
        <AddDescriptionContainer
          modelInfo={modelInfo}
          navigateTo={handleNavigateToGraph}
          updateFlag={false}
          handleCreateDataModel={handleCreateDataModel}
          onBack={handleBackToSidebar}
        />
      ),
      connection: <IntegrationPage />,
      ankoModel: <ViewMetricByModelContainer onNavigateToGraph={handleNavigateToGraph} />,
      thoughtspotEmbed: <ThoughtSpotEmbed />
    };

    return componentMapping[selectedLink] || null;
  };

  return (
    <ConfigurationContextProvider>
      <Grid
        templateAreas={`
          "sidebar main"
        `}
        gridTemplateRows="1fr"
        gridTemplateColumns={isSidebarCollapsed ? "48px 1fr" : "25% 75%"}
        height="100%"
        p={0}
      >
        <GridItem area="sidebar" bg="gray.50" borderRightWidth="1px" borderColor="gray.300">
          {isSidebarCollapsed ? (
            <VStack pt={4} spacing={0}>
              <IconButton
                aria-label="Expand sidebar"
                icon={<Icon as={TbLayoutSidebarLeftExpandFilled} boxSize={7} />}
                variant="ghost"
                size="lg"
                onClick={() => setIsSidebarCollapsed(false)}
                _hover={{ bg: "purple.50" }}
              />
            </VStack>
          ) : showMetricsInSidebar ? (
            <MetricListComponent
              onBack={handleBackToSidebar}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showBenchmarksInSidebar ? (
            <BenchmarkListComponent
              onBack={handleBackToSidebar}
              benchmarkList={benchmarkList}
              setBenchmarkList={setBenchmarkList}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showMeasuresInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={measureList}
              setKGColumnList={setMeasureList}
              columnType={ColumnType.MEASURE}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showAttributesInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={attributeList}
              setKGColumnList={setAttributeList}
              columnType={ColumnType.ATTRIBUTE}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showTemporalInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={temporalList}
              setKGColumnList={setTemporalList}
              columnType={ColumnType.TEMPORAL}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : (
            <VStack spacing="3" alignItems="stretch" px={1} py={1} mt={3}>
              <Box>
                <HStack
                  as="button"
                  type="button"
                  width="full"
                  spacing="3"
                  justifyContent="space-between"
                  py={2}
                  px={2}
                  borderRadius="md"
                  _hover={{ bg: "purple.50", color: "black" }}
                  onClick={() => setShowMKGNetwork(true)}
                  textAlign="left"
                >
                  <HStack spacing="3" flex={1} minW={0}>
                    <Icon
                      as={IoGitCompareOutline}
                      boxSize={6}
                      color="fg.default"
                      flexShrink={0}
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="md">
                      Metric Knowledge Graph
                    </Text>
                  </HStack>
                  <IconButton
                    aria-label="Collapse sidebar"
                    icon={<Icon as={TbLayoutSidebarRightExpandFilled} boxSize={7} />}
                    variant="ghost"
                    size="sm"
                    flexShrink={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSidebarCollapsed(true);
                    }}
                    _hover={{ bg: "purple.50" }}
                  />
                </HStack>
                <Collapse in={isKGOpen} animateOpacity>
                  <VStack spacing="3" alignItems="stretch" px={8} py={3}>
                    <HStack spacing="2">
                      <Icon as={IoIosReturnRight} boxSize={5} />
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={MdBarChart} boxSize={6} />}
                        _hover={{ bg: "purple.600" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Metrics
                          setShowMetricsInSidebar(true);
                          setShowBenchmarksInSidebar(false);
                          setShowKGColumnsInSidebar(false);
                        }}
                        bg={
                          showMetricsInSidebar
                            ? "purple.700"
                            : "purple.700"
                        }
                        color={
                          showMetricsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Metrics
                        </Text>
                      </Button>
                    </HStack>
                    <HStack spacing="2">
                      <Icon as={IoIosReturnRight} boxSize={5} />
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={PiTarget} boxSize={6} />}
                        _hover={{ bg: "orange.400" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Benchmarks
                          setShowBenchmarksInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowKGColumnsInSidebar(false);
                        }}
                        bg={
                          showBenchmarksInSidebar
                            ? "orange.500"
                            : "orange.500"
                        }
                        color={
                          showBenchmarksInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Benchmarks
                        </Text>
                      </Button>
                    </HStack>
                    <HStack spacing="2">
                      <Icon as={IoIosReturnRight} boxSize={5} />
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={MdNumbers} boxSize={6} />}
                        _hover={{ bg: "green.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Measures
                          setShowMeasuresInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "green.600"
                            : "green.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Measures
                        </Text>
                      </Button>
                    </HStack>
                    <HStack spacing="2">
                      <Icon as={IoIosReturnRight} boxSize={5} />
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={FaChartPie} boxSize={6} />}
                        _hover={{ bg: "blue.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Attributes
                          setShowAttributesInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "blue.600"
                            : "blue.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Attributes
                        </Text>
                      </Button>
                    </HStack>
                    <HStack spacing="2">
                      <Icon as={IoIosReturnRight} boxSize={5} />
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={FiCalendar} boxSize={6} />}
                        _hover={{ bg: "yellow.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Temporal
                          setShowTemporalInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "yellow.600"
                            : "yellow.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Datetime
                        </Text>
                      </Button>
                    </HStack>
                  </VStack>
                </Collapse>
              </Box>
              <Box px={2} py={1}>
                <Divider borderColor="gray.300" my={1} />
              </Box>
              <Box>
                <Button
                  variant="tertiary"
                  onClick={onToggle} // Just toggle the Integration section, don't hide MKG
                  justifyContent="space-between"
                  width="full"
                  _hover={{ bg: "purple.100", color: "black" }}
                >
                  <HStack spacing="3">
                    <Icon
                      as={HiWrenchScrewdriver}
                      boxSize={5}
                      color="fg.default"
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="md">
                      Connections
                    </Text>
                  </HStack>
                  <PopoverIcon isOpen={isOpen} />
                </Button>
                <Collapse in={isOpen} animateOpacity>
                  <Stack spacing="1" alignItems="stretch" ps="8" py="1">
                    <Button
                      variant="tertiary"
                      justifyContent="start"
                      _hover={{ bg: "purple.100", color: "black" }}
                      leftIcon={<Image src={Snowflake} boxSize="1.5rem" />}
                      onClick={() => {
                        setShowMKGNetwork(false); // Hide MKG when clicking Snowflake
                        setActiveButton("snowflake");
                        setSelectedLink("snowflake");
                      }}
                      bg={
                        activeButton === "snowflake" ? "purple.700" : undefined
                      }
                      color={
                        activeButton === "snowflake" ? "white" : "fg.default"
                      }
                    >
                      Snowflake
                    </Button>
                    <Button
                      variant="tertiary"
                      justifyContent="start"
                      _hover={{ bg: "purple.100", color: "black" }}
                      leftIcon={<Image src={Thoughtspot} boxSize="1.5rem" />}
                      onClick={() => {
                        setShowMKGNetwork(false); // Hide MKG when clicking Thoughtspot
                        setActiveButton("thoughtspot");
                        setSelectedLink("thoughtspot");
                      }}
                      bg={
                        activeButton === "thoughtspot"
                          ? "purple.700"
                          : undefined
                      }
                      color={
                        activeButton === "thoughtspot" ? "white" : undefined
                      }
                    >
                      Thoughtspot
                    </Button>
                    <Button
                      variant="tertiary"
                      justifyContent="start"
                      _hover={{ bg: "purple.100", color: "black" }}
                      leftIcon={<Icon as={RiFileExcel2Line} boxSize="1.5rem" />}
                      onClick={() => {
                        setShowMKGNetwork(false); // Hide MKG when clicking Excel
                        setActiveButton("excel");
                      }}
                      bg={activeButton === "excel" ? "purple.700" : undefined}
                      color={activeButton === "excel" ? "white" : "fg.default"}
                    >
                      Excel
                    </Button>
                  </Stack>
                </Collapse>
              </Box>
              <Box px={2} py={1}>
                <Divider borderColor="gray.300" my={1} />
              </Box>
              <Box>
                <Button
                  variant="tertiary"
                  justifyContent="space-between"
                  width="full"
                  _hover={{ bg: "purple.100", color: "black" }}
                  onClick={() => {
                    setShowMKGNetwork(false); // Hide MKG when clicking Connection
                    setActiveButton("connection");
                    setSelectedLink("connection");
                  }}
                >
                  <HStack>
                    <Icon
                      as={HiWrenchScrewdriver}
                      boxSize={5}
                      color="fg.default"
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="md">
                      Connections (New)
                    </Text>
                  </HStack>
                </Button>
              </Box>
              <Box px={2} py={1}>
                <Divider borderColor="gray.300" my={1} />
              </Box>
              <Box>
                <Button
                  variant="tertiary"
                  justifyContent="space-between"
                  width="full"
                  _hover={{ bg: "purple.100", color: "black" }}
                  onClick={() => {
                    setShowMKGNetwork(false); // Hide MKG when clicking Anko Model
                    setActiveButton("ankoModel");
                    setSelectedLink("ankoModel");
                  }}
                >
                  <HStack>
                    <Icon
                      as={FaDatabase}
                      boxSize={5}
                      color="fg.default"
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="md">
                      Data Models
                    </Text>
                  </HStack>
                </Button>
              </Box>
              {
                userId === "02c73738-9481-4d0f-85c4-e927c6a09d09" && (
                  <>
                    <Box px={2} py={1}>
                      <Divider borderColor="gray.300" my={1} />
                    </Box>
                    <Box>
                      <Button
                        variant="tertiary"
                        justifyContent="space-between"
                        width="full"
                        _hover={{ bg: "purple.100", color: "black" }}
                        onClick={() => {
                          setShowMKGNetwork(false); // Hide MKG when clicking ThoughtSpot
                          setActiveButton("thoughtspotEmbed");
                          setSelectedLink("thoughtspotEmbed");
                        }}
                        bg={
                          activeButton === "thoughtspotEmbed" ? "purple.700" : undefined
                        }
                        color={
                          activeButton === "thoughtspotEmbed" ? "white" : "fg.default"
                        }
                      >
                        <HStack>
                          <Image
                            src={Thoughtspot}
                            boxSize="1.25rem"
                          />
                          <Text as="span" color={activeButton === "thoughtspotEmbed" ? "white" : "fg.default"} fontWeight={600} fontSize="md">
                            ThoughtSpot
                          </Text>
                        </HStack>
                      </Button>
                    </Box>
                  </>
                )
              }
            </VStack>
          )}
        </GridItem>
        <GridItem
          area="main"
          overflowY="auto"
          height="100%"
          sx={{
            "::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none", // IE and Edge
            scrollbarWidth: "none", // Firefox
          }}
        >
          <Stack p={0} height="100%">{renderComponent()}</Stack>
        </GridItem>
      </Grid>
    </ConfigurationContextProvider>
  );
};

export default ConfigurationPage;

export const PopoverIcon = (props: { isOpen: boolean }) => {
  const iconStyles = {
    transform: props.isOpen ? "rotate(-180deg)" : undefined,
    transition: "transform 0.2s",
    transformOrigin: "center",
  };
  return <Icon aria-hidden as={FiChevronDown} __css={iconStyles} />;
};
